import type { SortDir, WidgetCapabilities } from '@developerz/domain';
import { createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { Field } from './Field';
import { Select } from './Select';
import { Button } from './ui/Button';

// Data-widget toolbar (#977) — the controls a widget's DECLARED capabilities
// earn, and only those: a sort-key picker + direction toggle when `sort` names
// keys, a filter-key picker + `eq` value input when `filter` does. Keys render
// verbatim: they are the result-set keys the server fences the pager body
// against, and a prettified label would be a second name for one thing.
// Presenter — the signals and the reset-to-page-one live in <DataWidget/>.

export type WidgetControlsProps = {
  readonly capabilities: WidgetCapabilities;
  readonly sortKey: string;
  readonly sortDir: SortDir;
  readonly filterKey: string;
  readonly filterValue: string;
  readonly onSortKey: (key: string) => void;
  readonly onSortDir: (dir: SortDir) => void;
  readonly onFilterKey: (key: string) => void;
  readonly onFilterValue: (value: string) => void;
};

export function WidgetControls(props: WidgetControlsProps): JSX.Element {
  // A board renders many widgets, each with this toolbar: fixed ids would
  // collide and name the wrong control.
  const uid = createUniqueId();
  return (
    <div class="filter-row">
      <Show when={props.capabilities.sort.length > 0}>
        <Field label={t('widgets.sort_label')} id={`widget-sort-${uid}`}>
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('widgets.sort_label')}
              value={props.sortKey}
              options={[
                { value: '', label: t('widgets.sort_default') },
                ...props.capabilities.sort.map((key) => ({ value: key, label: key })),
              ]}
              onChange={props.onSortKey}
            />
          )}
        </Field>
        <Button
          variant="outline"
          size="sm"
          disabled={props.sortKey === ''}
          onClick={() => props.onSortDir(props.sortDir === 'asc' ? 'desc' : 'asc')}
        >
          {props.sortDir === 'asc' ? t('widgets.sort_asc') : t('widgets.sort_desc')}
        </Button>
      </Show>
      <Show when={props.capabilities.filter.length > 0}>
        <Field label={t('widgets.filter_label')} id={`widget-filter-key-${uid}`}>
          {(a11y) => (
            <Select
              a11y={a11y}
              label={t('widgets.filter_label')}
              value={props.filterKey}
              options={[
                { value: '', label: t('widgets.filter_any') },
                ...props.capabilities.filter.map((key) => ({ value: key, label: key })),
              ]}
              onChange={props.onFilterKey}
            />
          )}
        </Field>
        <Field label={t('widgets.filter_value_label')} id={`widget-filter-value-${uid}`}>
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="text"
              placeholder={t('widgets.filter_value_placeholder')}
              value={props.filterValue}
              disabled={props.filterKey === ''}
              onChange={(e) => props.onFilterValue(e.currentTarget.value)}
            />
          )}
        </Field>
      </Show>
    </div>
  );
}
